import { twJoin } from 'tailwind-merge';

import { Label } from '@/core/tamagoshiTailwind/components/Label/Label';
import {
  Div,
  Span,
} from '@/core/tamagoshiTailwind/components/Typography/Typography';
import { useDesignSystem } from '@/core/tamagoshiTailwind/context/context';
import { type Locale } from '@/core/tamagoshiTailwind/interfaces';

import { priceVariantParams } from './Price.constants';
import { type PriceProps } from './Price.interfaces';

// NOTE Spanish RAE specifies that only numbers bigger than 4 digits will be
//      formatted with the thousands dot separator eg. 1234,00
//      See https://www.rae.es/dpd/n%C3%BAmeros
//      But business wants the following separation as other websites do
//      eg. 1.234,00
//      Even if is not what the language academy specifies.
//      For that reason we reuse the german formatter for spanish language
const getFormatterLocale = (locale: Locale): Locale =>
  locale === 'es-ES' ? 'de-DE' : locale;

/** Format the price based on the locale and currency */
const formatPrice = (price: number, locale: Locale) =>
  new Intl.NumberFormat(getFormatterLocale(locale), {
    style: 'currency',
    currency: locale === 'en-GB' ? 'GBP' : 'EUR',
  })
    .format(price)
    // NOTE Remove the space before the currency symbol for Euros
    .replace(/\s(?=€)/g, '');

/**
 * Price component to display the product prices.
 *
 * The `market` property controls the appearance of the component. Wrapping it
 * with the usual market class name has little to no effect.
 *
 * Use the translation keys available in Phrase for the VAT labels
 * - `designSystem.price.vatInclLabel`
 * - `designSystem.price.vatExclLabel`
 *
 * See the related [Figma][figma] for more details.
 *
 * [figma]: https://www.figma.com/design/KAiRlBccr6bFYmKby9wQrR?node-id=15439-3558
 */
export const Price = ({
  currentPriceVatExcl,
  currentPriceVatIncl,
  direction,
  discountPercentage,
  market,
  measurementUnit,
  originalPriceVatExcl,
  originalPriceVatIncl,
  size,
  unitPriceVatExcl,
  unitPriceVatIncl,
  vatExclLabel,
  vatInclLabel,
}: PriceProps) => {
  const { locale } = useDesignSystem();

  const { priceScale, typoScale, vatScale } = priceVariantParams[size];

  const integerDiscountPercentage = discountPercentage
    ? Math.round(discountPercentage)
    : 0;
  const isPromotion =
    1 <= integerDiscountPercentage && integerDiscountPercentage <= 100;
  const discountValue = isPromotion ? integerDiscountPercentage : null;
  const originalPrice =
    market === 'b2b' ? originalPriceVatExcl : originalPriceVatIncl;

  return (
    <div>
      <div
        className={twJoin(
          'flex',
          direction === 'line' ? 'items-center gap-xs' : 'flex-col',
        )}
      >
        {/* CURRENT PRICE BLOCK */}
        <div
          className={twJoin(
            'flex items-center',
            isPromotion && 'text-promotion-primary',
          )}
        >
          <Div scale={priceScale} weight="bold">
            {formatPrice(currentPriceVatExcl ?? currentPriceVatIncl, locale)}
          </Div>
          {market === 'b2b' && (
            <Span
              scale={vatScale}
              weight="bold"
              className="self-start whitespace-nowrap"
            >
              {vatExclLabel}
            </Span>
          )}
          {isPromotion && (
            <div className="ml-xs flex">
              <Label variant="PromotionPrimary" text={`-${discountValue}%`} />
            </div>
          )}
        </div>

        {/* ORIGINAL PRICE BLOCK */}
        {originalPrice && (
          <Div className="flex" scale={typoScale}>
            <span className="line-through">
              {formatPrice(originalPrice, locale)}
            </span>
            {market === 'b2b' && <Span scale="tiny">{vatExclLabel}</Span>}
          </Div>
        )}
      </div>

      {market === 'b2b' && (
        <Div scale={typoScale}>
          <div className="flex">
            <span>{formatPrice(currentPriceVatIncl, locale)}</span>
            <Span scale="tiny">{vatInclLabel}</Span>
          </div>
          {(size === 'XXL' || size === 'XL') && (
            <>
              {unitPriceVatExcl && (
                <div className="flex">
                  <span>{formatPrice(unitPriceVatExcl, locale)}</span>
                  <Span scale="tiny">{vatExclLabel}</Span>
                  <span>/{measurementUnit}</span>
                </div>
              )}
              {unitPriceVatIncl && (
                <div className="flex">
                  <span>{formatPrice(unitPriceVatIncl, locale)}</span>
                  <Span scale="tiny">{vatInclLabel}</Span>
                  <span>/{measurementUnit}</span>
                </div>
              )}
            </>
          )}
        </Div>
      )}

      {market === 'b2c' && unitPriceVatIncl && (
        <Div className="flex" scale={typoScale}>
          <span>{formatPrice(unitPriceVatIncl, locale)}</span>
          <span>/{measurementUnit}</span>
        </Div>
      )}
    </div>
  );
};

Price.displayName = 'Price';
